import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { type CSSProperties, type ForwardedRef, forwardRef, useContext } from 'react';
import { useSwimlaneEventPublisher } from '../../api';
import { SwimlaneStateContext, useSwimlaneRootProps } from '../../context';
import {
  AXIS_HEIGHT,
  CARD_WIDTH,
  COLUMN_HEADER_HEIGHT,
  COMPACT_CARD_WIDTH,
  GRID_GAP,
  ROW_HEADER_WIDTH,
  ROW_HEIGHT_MIN,
} from '../constants';

const baseContainer: CSSProperties = {
  lineHeight: '32px',
  zIndex: 10,
  boxSizing: 'content-box',
};

const ColumnAxisContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isHeadersSticky' &&
    prop !== 'backgroundColor' &&
    prop !== 'isPngPublicationTemplate' &&
    prop !== 'isPdfExportTemplate',
})<{
  isHeadersSticky: boolean;
  backgroundColor: string;
  isPngPublicationTemplate?: boolean;
  isPdfExportTemplate?: boolean;
}>(({ isHeadersSticky, backgroundColor, isPngPublicationTemplate, isPdfExportTemplate }) => ({
  ...baseContainer,
  position:
    isHeadersSticky && !isPngPublicationTemplate && !isPdfExportTemplate ? 'sticky' : 'relative',
  cursor: 'pointer',
  gridArea: 'axisX',
  height: '32px',
  top: '0px',
  // Border height matches GRID_GAP (16px) to visually fill the gap between axis label and column headers
  borderBottom: `16px solid ${backgroundColor}`,
  backgroundColor,
}));

const RowAxisContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isHeadersSticky' &&
    prop !== 'backgroundColor' &&
    prop !== 'isPngPublicationTemplate' &&
    prop !== 'isPdfExportTemplate',
})<{
  isHeadersSticky: boolean;
  backgroundColor: string;
  isPngPublicationTemplate?: boolean;
  isPdfExportTemplate?: boolean;
}>(({ isHeadersSticky, backgroundColor, isPngPublicationTemplate, isPdfExportTemplate }) => ({
  ...baseContainer,
  position:
    isHeadersSticky && !isPngPublicationTemplate && !isPdfExportTemplate ? 'sticky' : 'relative',
  gridArea: 'axisY',
  cursor: 'pointer',
  width: '32px',
  left: '0px',
  borderRight: `16px solid ${backgroundColor}`,
}));

const getBaseAxisStyles = (): CSSProperties => ({
  display: 'flex',
  // Always use light gray background for axis labels - they should remain visible even in transparent mode
  backgroundColor: '#F5F5F5',
  borderRadius: '8px',
  border: '1px solid #E2E2E2',
});

const ColumnAxis = styled('span')(getBaseAxisStyles);

const RowAxis = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isPngPublicationTemplate',
})<{ isPngPublicationTemplate?: boolean }>(({ isPngPublicationTemplate }) => {
  const baseStyles = getBaseAxisStyles();
  return {
    ...baseStyles,
    // Override baseAxis display: flex to allow rotated text rendering in export mode
    display: isPngPublicationTemplate ? 'block' : baseStyles.display,
    height: '100%',
  };
});

const baseLabel: CSSProperties = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  fontWeight: 400,
  letterSpacing: '1px',
};

const ColumnAxisLabel = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'isHeadersSticky' &&
    prop !== 'isPngPublicationTemplate' &&
    prop !== 'isPdfExportTemplate',
})<{ isHeadersSticky: boolean; isPngPublicationTemplate?: boolean; isPdfExportTemplate?: boolean }>(
  ({ isHeadersSticky, isPngPublicationTemplate, isPdfExportTemplate }) => ({
    ...baseLabel,
    position:
      isHeadersSticky && !isPngPublicationTemplate && !isPdfExportTemplate ? 'sticky' : 'relative',
  }),
);

const RowAxisLabel = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'isHeadersSticky' &&
    prop !== 'isPngPublicationTemplate' &&
    prop !== 'isPdfExportTemplate',
})<{ isHeadersSticky: boolean; isPngPublicationTemplate?: boolean; isPdfExportTemplate?: boolean }>(
  ({ isHeadersSticky, isPngPublicationTemplate, isPdfExportTemplate }) => ({
    writingMode: 'vertical-lr',
    transform: 'rotate(180deg)',
    position:
      isHeadersSticky && !isPngPublicationTemplate && !isPdfExportTemplate ? 'sticky' : 'relative',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    fontWeight: 400,
    letterSpacing: '1px',
    textAlign: 'center',
    height: '100%',
    // Conditionally apply overflow based on context
    ...(isPngPublicationTemplate
      ? {
          // PNG export: allow overflow for rotated text to prevent cutoff
          overflow: 'visible',
        }
      : {
          // Interactive UI: constrain overflow with ellipsis
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }),
  }),
);

export const SwimlaneAxisLabelTop = forwardRef((_props, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    columnAxisLabelText,
    rowAxisLabelText,
    rowHeaderOrientation,
    rowHeadersWidth,
    isHeadersSticky,
    columnHeaders,
    isCompact,
    backgroundColor,
    isPngPublicationTemplate,
    isPdfExportTemplate,
  } = useSwimlaneRootProps();
  const publishEvent = useSwimlaneEventPublisher();

  const columnAxisLabelWidth = getColumnAxisLabelWidth(columnHeaders.length, isCompact);

  const rowHeaderOffset =
    rowHeaderOrientation === 'horizontal' ? rowHeadersWidth : ROW_HEADER_WIDTH.vertical.value;
  const widthOffset =
    typeof rowAxisLabelText === 'string'
      ? rowHeaderOffset + GRID_GAP.value + AXIS_HEIGHT.value
      : rowHeaderOffset;

  if (columnAxisLabelText) {
    return (
      <ColumnAxisContainer
        className="fs-exclude"
        isHeadersSticky={!!isHeadersSticky}
        isPngPublicationTemplate={isPngPublicationTemplate}
        isPdfExportTemplate={isPdfExportTemplate}
        backgroundColor={backgroundColor}
        role="columnheader"
        data-export="column-header-container"
        data-testid="main-columnheader"
        sx={{ width: `${columnAxisLabelWidth}px` }}
      >
        <ColumnAxis
          style={!isPngPublicationTemplate ? {} : { justifyContent: 'center' }}
          data-testid="column-axis"
        >
          <ColumnAxisLabel
            ref={ref}
            isHeadersSticky={!!isHeadersSticky}
            isPngPublicationTemplate={isPngPublicationTemplate}
            isPdfExportTemplate={isPdfExportTemplate}
            style={
              isPdfExportTemplate
                ? { width: '100%' }
                : !isPngPublicationTemplate
                  ? {
                      width: `calc(100vw - ${widthOffset}px)`,
                      left: `${widthOffset}px`,
                    }
                  : {
                      alignSelf: 'center',
                    }
            }
            onClick={(e) => {
              publishEvent('axisLabelClicked', { axis: 'column', anchorEl: e.currentTarget });
            }}
          >
            {columnAxisLabelText}
          </ColumnAxisLabel>
        </ColumnAxis>
      </ColumnAxisContainer>
    );
  }
  return null;
});

export const SwimlaneAxisLabelLeft = forwardRef((_props, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    rowAxisLabelText,
    columnAxisLabelText,
    isHeadersSticky,
    rowHeaders,
    backgroundColor,
    isPngPublicationTemplate,
    isPdfExportTemplate,
  } = useSwimlaneRootProps();
  const swimlaneStateContext = useContext(SwimlaneStateContext);
  const publishEvent = useSwimlaneEventPublisher();

  if (!rowAxisLabelText) {
    return null;
  }

  const heightOffset =
    typeof columnAxisLabelText === 'string'
      ? AXIS_HEIGHT.value + GRID_GAP.value + COLUMN_HEADER_HEIGHT.value
      : COLUMN_HEADER_HEIGHT.value;
  const nonGroupRowHeaders = rowHeaders.filter(
    (h) => !('isGroupHeader' in h && h.isGroupHeader === true),
  );
  const rowAxisHeight = swimlaneStateContext?.getRowHeight
    ? getRowAxisLabelWidth(
        nonGroupRowHeaders.map((header) => header.id),
        swimlaneStateContext.getRowHeight,
      )
    : 100; // use fallback of 100 if getRowHeight is not available

  return (
    <RowAxisContainer
      className="fs-exclude"
      isHeadersSticky={!!isHeadersSticky}
      isPngPublicationTemplate={isPngPublicationTemplate}
      isPdfExportTemplate={isPdfExportTemplate}
      backgroundColor={backgroundColor}
      role="rowheader"
      data-export="row-header"
      data-roadmap-y-axis-label="true"
      data-testid="main-rowheader"
      sx={{
        height: `${rowAxisHeight}px`,
      }}
    >
      <RowAxis data-testid="row-axis" isPngPublicationTemplate={isPngPublicationTemplate}>
        <RowAxisLabel
          ref={ref}
          isHeadersSticky={!!isHeadersSticky}
          isPngPublicationTemplate={isPngPublicationTemplate}
          isPdfExportTemplate={isPdfExportTemplate}
          style={
            !isPngPublicationTemplate
              ? {
                  // Position row axis label: Use smaller of calculated height or viewport (100vh)
                  // Offset by heightOffset for multiple rows, but only if content height exceeds minimum
                  // ROW_HEIGHT_MIN ensures label doesn't position incorrectly for very short content
                  height: `calc(min(100vh, ${rowAxisHeight}px) - ${rowAxisHeight > ROW_HEIGHT_MIN.value ? heightOffset : 0}px)`,
                  // if rowAxisHeight is less than heightOffset, don't set top
                  ...(rowAxisHeight > heightOffset ? { top: `${heightOffset}px` } : {}),
                }
              : {}
          }
          onClick={(e) => {
            publishEvent('axisLabelClicked', { axis: 'row', anchorEl: e.currentTarget });
          }}
        >
          {rowAxisLabelText}
        </RowAxisLabel>
      </RowAxis>
    </RowAxisContainer>
  );
});

export function getColumnAxisLabelWidth(columnsCount: number, isCompact: boolean): number {
  const totalGap = GRID_GAP.value * (columnsCount - 1);
  const columnAxisLabelWidth =
    columnsCount * (isCompact ? COMPACT_CARD_WIDTH.value : CARD_WIDTH.value) + totalGap;
  return columnAxisLabelWidth;
}

export function getRowAxisLabelWidth(
  rowHeaderIDs: string[],
  getRowHeight: (rowID: string) => number,
): number {
  let rowAxisHeight = 0;
  if (!getRowHeight) {
    return rowAxisHeight;
  }
  rowHeaderIDs.forEach((headerID) => {
    rowAxisHeight = rowAxisHeight + getRowHeight(headerID);
  });

  rowAxisHeight = rowAxisHeight + GRID_GAP.value * (rowHeaderIDs.length - 1);
  return rowAxisHeight;
}
